import { AfterViewChecked, AfterViewInit, Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CreateRuleComponent, EditRuleDialogData } from '../create-rule/create-rule.component';
import { GlobalFunctions, imagePath, imgTypes } from '@global/GlobalFunctions';
import { ActivatedRoute, Router } from '@angular/router';
import { AssetsDataService, LevelAsset } from '@services/assets-data.service';
import { RulesDataService } from '@services/rules-data.service';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { combineLatest, distinctUntilChanged, filter, finalize, Subject, takeUntil } from 'rxjs';
import { RuleEvent } from '@services/events-data.service';
import { AccountsService, IAccountUser } from '@services/accounts.service';
import { HttpErrorResponse } from '@angular/common/http';
import { Action } from '@services/actions-data.service';
import { OrganisationsHierarchy } from '@services/organisation-data.service';
import { DropDownList } from '@syncfusion/ej2-angular-dropdowns';
import { isBreakLabel } from '@syncfusion/ej2-angular-charts';

/** assetSearchDialogData  */
export interface assetSearchDialogData {
	assetList: LevelAsset[];
	hierarchy: OrganisationsHierarchy[];
	currentLocation?: OrganisationsHierarchy;
}

@Component({
	selector: 'app-asset-search-dialog',
	templateUrl: './asset-search-dialog.component.html',
	styleUrls: ['./asset-search-dialog.component.scss']
})
export class AssetSearchDialogComponent implements OnInit, AfterViewInit {

	/** Path to Assets Images */
	public imagePathAssets(): string {
		return imagePath(this.selectedAccountUID, imgTypes.organisationassets).toLowerCase()
	}

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** Selected AccountUID */
	public selectedAccountUID: string;

	/** Selected Organisation ID */
	public selectedOrganisationUID: string;

	/** filteredAssetList */
	public filteredAssetList: LevelAsset[] = [];

	/** assetList */
	public assetList: LevelAsset[] = [];

	/** Form Group */
	dialogForm: FormGroup;

	/** fields */
	public fields: Object = { text: "Name", value: "AssetID" };

	/** selectedAsset */
	public selectedAsset: LevelAsset;

	public selectedLocation?: OrganisationsHierarchy;

	public MainLocations: OrganisationsHierarchy[] = [];

	/** locationFields */
	public locationFields: { text: string, value: string } = { text: "Name", value: "Id" };

	private _dropdown: DropDownList;

	/** dropdown */
	@ViewChild('dropdown')
	set dropdown(dropdown: DropDownList) {
		try {
			if (!dropdown) { return; }
			this._dropdown = dropdown;
			dropdown.showClearButton = true;
		} catch (error) {
			console.error('Error assigning dropdown:', error);
		}
	}

	private _locationDropdown: DropDownList;
	/** locationDropdown */
	@ViewChild('locationDropdown')
	set locationDropdown(dropdown: DropDownList) {
		try {
			if (!dropdown) { return; }
			this._locationDropdown = dropdown;
			dropdown.showClearButton = true;
		} catch (error) {
			console.error('Error assigning location Dropdown:', error);
		}
	}

	/** hierarchy */
	public hierarchy: OrganisationsHierarchy[] = [];

	/** assetPath */
	public assetPath: OrganisationsHierarchy[] = [];

	/** currentMainLocation */
	public currentMainLocation?: OrganisationsHierarchy;

	/** input */
	@ViewChild('input') input!: ElementRef;

	constructor(
		private fb: FormBuilder,
		public dialogRef: MatDialogRef<CreateRuleComponent>,
		private globalFunctions: GlobalFunctions,
		private router: Router,
		private route: ActivatedRoute,
		private assetsDataService: AssetsDataService,
		private siteSubscriptions: siteSubscriptions,
		private rulesDataService: RulesDataService,
		private authenticationService: AuthenticationService,
		private accountsService: AccountsService,
		@Inject(MAT_DIALOG_DATA) public dialogData: assetSearchDialogData,
	) {
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			filter(([selectedAccountGUID, selectedOrganisationUID]) =>
				!!selectedAccountGUID && !!selectedOrganisationUID
			)
		).subscribe({
			next: ([selectedAccountGUID, selectedOrganisationUID]) => {
				this.selectedAccountUID = selectedAccountGUID;
				this.selectedOrganisationUID = selectedOrganisationUID;
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: combined route parameters", false);
			}
		});
	}

	/**
	 * ngAfterViewInit
	 */
	ngAfterViewInit(): void {
		if (this.locationDropdown && this.currentMainLocation) {
			this.locationDropdown.value = this.currentMainLocation.Id;
		}
		// setTimeout(() => {
		// 	this.input.nativeElement.focus();
		// }, 0);
	}

	/**
	 * Page On Initialise Event Handler
	 */
	ngOnInit(): void {
		if (this.dialogData.assetList) {
			this.assetList = this.dialogData.assetList;
			this.filteredAssetList = this.assetList;
		}

		if (this.dialogData.hierarchy) {
			this.hierarchy = this.dialogData.hierarchy;
			this.MainLocations = [];
			if (this.hierarchy[0].SubChild) {
				this.hierarchy[0].SubChild.forEach(subchild => {
					this.MainLocations.push(subchild);
				});
			}
		}

		if (this.dialogData.currentLocation) {
			this.findCurrentMainLocation(this.dialogData.currentLocation);
		}

		this.dialogForm = this.fb.group({
			asset: ['', Validators.required]
		});
	}

	/**
	 * Cancel button for dialog
	 */
	onCancelClick(): void {
		this.dialogRef.close();
	}

	/**
	 * selectAsset
	 * @param event event
	 */
	selectAsset(event: any) {
		this.selectedAsset = event.itemData;
		this.assetPath = this.getAssetPath(this.selectedAsset);
	}

	/**
	 * View the Image Fullsize
	 * @param imageURL
	 */
	viewImage(imageURL: string) {
		this.globalFunctions.viewImageFullSize(imageURL);
	}

	/**
	 * onFiltering
	 * @param event
	 */
	onFiltering(event: any): void {
		this.dropdown.showPopup();
		event.cancel = true;
		const query = event.target.value.toLowerCase();
		this.filterFavouriteAssets();
		// this.filterFavouriteAssets();
		this.filteredAssetList = this.filteredAssetList.filter(data => {
			const name = `${data.Name}`.toLowerCase();
			const description = `${data.Description}`.toLowerCase() || '';

			const combinedStr = `${name} ${description}`;
			return combinedStr.includes(query);
		});
	}

	/**
	 * filterByLocation
	 * @param event event
	 * @param cancel cancel
	 */
	filterByLocation(event: any, cancel?: boolean) {
		if (cancel) {
			this.selectedLocation = undefined;
			this.locationDropdown.clear();
		} else if (event.itemData) {
			this.selectedLocation = <OrganisationsHierarchy>event.itemData;
		}
		this.filterFavouriteAssets();
	}

	/**
	 * filterFavouriteAssets
	 */
	filterFavouriteAssets() {
		this.filteredAssetList = this.assetList;
		if (this.selectedLocation) {
			let locationAssets: LevelAsset[] = this.flattenAssets([this.selectedLocation]);
			let locationAssetIDs: number[] = [];
			locationAssets.forEach(asset => {
				locationAssetIDs.push(asset.AssetID);
			});
			this.filteredAssetList = this.filteredAssetList.filter((x: LevelAsset) => locationAssetIDs.includes(x.AssetID));
		} else {
			this.filteredAssetList = this.filteredAssetList;
		}
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * flattenSubLocations
	 * @param hierarchy hierarchy
	 * @returns
	 */
	flattenSubLocations(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.SubChild && item.SubChild.length > 0) {
				result = result.concat(item.SubChild);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenSubLocations(item.SubChild));
		}

		return result;
	}

	/**
	 * getAssetPath
	 * @param asset asset
	 * @returns
	 */
	getAssetPath(asset: LevelAsset): OrganisationsHierarchy[] {
		if (!asset) {
			return [];
		}
		let result: OrganisationsHierarchy[] = [];
		let subLocations = this.flattenSubLocations(this.hierarchy);
		if (subLocations) {
			let assetLocation = subLocations.find((x: OrganisationsHierarchy) => x.Uid == asset.ParentUID);
			if (assetLocation) {
				let flag: boolean = true;
				let checkingLocation: OrganisationsHierarchy = assetLocation;
				while (flag) {
					if (!checkingLocation) {
						flag = false;
						break;
					}
					result.push(checkingLocation);
					if (checkingLocation.ParentUid) {
						checkingLocation = subLocations.find((x: OrganisationsHierarchy) => x.Uid == checkingLocation.ParentUid);
					} else {
						flag = false;
					}
				}
				result.push(this.hierarchy[0]);
			}
		}
		return result.reverse();
	}

	/**
	 * goToFunctionalLocation
	 */
	goToFunctionalLocation(location: OrganisationsHierarchy) {
		this.dialogRef.close();
		this.router.navigate([`/${this.selectedAccountUID}/organisation/${this.selectedOrganisationUID}/location/${location.Uid}`]);
	}

	/**
	 * findCurrentMainLocation
	 * @param currentLocation currentLocation
	 */
	findCurrentMainLocation(currentLocation: OrganisationsHierarchy) {
		let subLocations = this.flattenSubLocations(this.hierarchy);
		if (subLocations) {
			let flag: boolean = true;
			let checkingLocation = currentLocation;
			let mainLocationUIDs: string[] = [];
			this.MainLocations.forEach(location => {
				mainLocationUIDs.push(location.Uid);
			})
			while (flag) {
				if (checkingLocation) {
					if (mainLocationUIDs.includes(checkingLocation.Uid)) {
						this.currentMainLocation = checkingLocation;
						flag = false;
						break;
					} else {
						checkingLocation = subLocations.find((x: OrganisationsHierarchy) => x.Uid == checkingLocation.ParentUid);
					}
				} else {
					flag = false;
					break;
				}
			}
		}
	}
}
